// BMO venue lookup against POST /api/location/venues — the client half of the
// optional "where are you playing?" step on BMO bets and BMO DFS entries. Same
// sample collection as the cash check (collectFixes), a different server route
// with its own rate budget and no fence: the server answers with every venue
// the player is inside of (in_range) plus the nearest out-of-range ones (nearby).
//
// State is MODULE-SCOPED on purpose: a picker reopened within the pass TTL
// shows the last result instead of asking for location again. Nothing here runs
// during SSR — lookup() is only ever called from a click, and the picker that
// reads the state is never server-rendered.
import { useNativeBridge } from './useNativeBridge'
import { collectFixes, clientTimezone } from './useLocationCheck'

export type VenuePurpose = 'bmo_bet' | 'bmo_dfs'
export type VenueLookupStatus =
  | 'idle' | 'locating' | 'checking'
  | 'in_venue' | 'no_venue' | 'location_needed' | 'spoof_suspected' | 'error'

export interface VenueSite { site_id: number; name: string; distance_mi: number; radius_mi: number }
export interface HomeSite { site_id: number; name: string }

interface VenuesOut {
  status: string
  reason_codes?: string[]
  in_range?: VenueSite[]
  nearby?: VenueSite[]
  home?: HomeSite
  expires_at?: string | null
  checked_at?: string | null
  ttl_sec?: number
}

// site 1 = home, the choice that needs no location; the server echoes it as
// `home` and may rename it, so the picker renders whatever came back last.
// (module-internal: only venueChoiceExpiry is exported besides the composable,
// so no generic names leak into nuxt's global auto-import namespace)
const HOME_SITE: HomeSite = { site_id: 1, name: 'play at home' }
// mirrors GEO_PASS_TTL_SEC's default — used only until a lookup reports the real one
const DEFAULT_TTL_SEC = 600

const RESULT_STATUSES: readonly VenueLookupStatus[] =
  ['in_venue', 'no_venue', 'location_needed', 'spoof_suspected']

// ---- module-scoped, shared across all callers --------------------------------
const status = ref<VenueLookupStatus>('idle')
const inRange = ref<VenueSite[]>([])
const nearby = ref<VenueSite[]>([])
const home = ref<HomeSite>(HOME_SITE)
const reasonCodes = ref<string[]>([])
const deniedForever = ref(false)   // native only: canAskAgain === false -> offer open settings
const expiresAt = ref<string | null>(null)
const checkedAt = ref<string | null>(null)
const ttlSec = ref<number>(DEFAULT_TTL_SEC)
// bumped once per COMPLETED lookup (success or error). the picker keys its
// auto-select on it so a result fires at most once, and never for a result that
// already existed when the picker opened.
const seq = ref(0)
let completedAtMs = 0
let inflight: Promise<VenueLookupStatus> | null = null

function expiryMs(): number {
  // server expiry first (the server validates a venue pick against it), then
  // checked_at + ttl, then our own completion clock — whichever exists
  if (expiresAt.value) {
    const t = Date.parse(expiresAt.value)
    if (!Number.isNaN(t)) return t
  }
  if (checkedAt.value) {
    const t = Date.parse(checkedAt.value)
    if (!Number.isNaN(t)) return t + ttlSec.value * 1000
  }
  return completedAtMs ? completedAtMs + ttlSec.value * 1000 : 0
}

// a usable, unexpired venue answer (in_venue or no_venue) is on file
function isFresh(): boolean {
  if (status.value !== 'in_venue' && status.value !== 'no_venue') return false
  return expiryMs() > Date.now()
}

// back to the idle prompt; keeps the last ttl (still the best guess). used when
// the server rejects a remembered venue (venue_not_verified) and when a picker
// opens onto a stale non-result state.
function reset() {
  status.value = 'idle'
  inRange.value = []
  nearby.value = []
  reasonCodes.value = []
  deniedForever.value = false
  expiresAt.value = null
  checkedAt.value = null
  completedAtMs = 0
}

// expiry for a remembered play-site choice: a venue lasts exactly as long as
// the server pass that backs it (the bet/entry route validates site_id against
// that lookup), home lasts one TTL from now
export function venueChoiceExpiry(siteId: number): string {
  if (siteId > 1 && expiresAt.value && !Number.isNaN(Date.parse(expiresAt.value))) return expiresAt.value
  return new Date(Date.now() + ttlSec.value * 1000).toISOString()
}

export function useVenueLookup() {
  const api = useApi()
  const bridge = useNativeBridge()

  async function lookup(purpose: VenuePurpose): Promise<VenueLookupStatus> {
    // single-flight: a double tap on "share my location" must not spend two
    // budget hits (and two rounds of upstream calls) on one answer
    if (inflight) return inflight
    inflight = (async () => {
      status.value = 'locating'
      reasonCodes.value = []
      inRange.value = []
      nearby.value = []
      deniedForever.value = false
      expiresAt.value = null
      checkedAt.value = null

      const collected = await collectFixes(bridge)
      deniedForever.value = collected.deniedForever

      status.value = 'checking'
      try {
        const out = await api.post<VenuesOut>('/location/venues', {
          purpose,
          source: collected.source,
          permission: collected.permission,
          samples: collected.samples,
          timezone: clientTimezone(),
          mocked: collected.mocked,
        })
        const s = out.status as VenueLookupStatus
        status.value = RESULT_STATUSES.includes(s) ? s : 'error'
        reasonCodes.value = out.reason_codes || []
        inRange.value = out.in_range || []
        nearby.value = out.nearby || []
        if (out.home && typeof out.home.site_id === 'number' && out.home.name) home.value = out.home
        expiresAt.value = out.expires_at ?? null
        checkedAt.value = out.checked_at ?? null
        if (typeof out.ttl_sec === 'number' && out.ttl_sec > 0) ttlSec.value = out.ttl_sec
      } catch (e: any) {
        // 503 = provider trouble (retryable); 429 = the BMO lookup budget; both -> error state
        status.value = 'error'
        const code = e?.status ?? e?.statusCode ?? e?.response?.status
        if (code === 429) reasonCodes.value = ['RATE_LIMITED']
      } finally {
        completedAtMs = Date.now()
        seq.value += 1
        inflight = null
      }
      return status.value
    })()
    return inflight
  }

  function openSettings() {
    bridge.openLocationSettings()
  }

  return {
    status, inRange, nearby, home, reasonCodes, deniedForever, expiresAt, checkedAt, ttlSec, seq,
    lookup, isFresh, reset, openSettings,
  }
}
